import {
  APIResponse,
  Result,
  PipelineState,
  DeploymentProperties,
  TagResponse,
  BitBucketPipelinesYaml,
  Selector,
  BranchingModelResponse,
  FeatureJson,
  FeatureJsonProperties,
  FeatureJsonComponent
} from './types'
import { Branch, developTag, releaseTag, configRepository, configFile, State } from './const'
import {
  apiUser,
  apiSetCommitTag,
  apiPushFile,
  apiPipeline,
  apiGetHEADofBranch,
  apiStartPipeline,
  apiGetLatestTag,
  apiGetBranches,
  apiGetFile,
  apiGetLatestMasterTags,
  apiGetLatestDevelopTags,
  apiGetLatestFeatureTags,
  apiCreatePullRequest,
  apiMergePullRequest,
  apiDiffStat,
  apiGetBranchModel
} from './api'
import * as yaml from 'yaml'

//TODO: refactor function to only return Result but with a default error message
function createResponse<T>(result: T): Result<T> {
  return {
    success: true,
    result: result as T
  }
}

export async function getUser() {
  const response = await apiUser()
  if (response.success) {
    return createResponse(response.result)
  } else {
    return response
  }
}

//functional calls
export async function isAuthenticated() {
  const response = await apiUser()
  return response.success
}

export async function isHeadTagged(
  repository: string,
  branch: string,
  tags: TagResponse[]
): Promise<Result<TagResponse | false> | APIResponse> {
  const commitResponse = await apiGetHEADofBranch(repository, branch)
  if (commitResponse.success) {
    const commit = commitResponse.result.target.hash
    const match = tags.find(tagObj => tagObj.commit === commit)
    return match ? createResponse(match) : createResponse(false)
  } else {
    return commitResponse
  }
}

export async function getBranches(repository: string): Promise<Result<string[]> | APIResponse> {
  function sortBranches(branches: string[]) {
    const category: any = {}
    let toReturn: string[] = []
    branches.forEach((branch: string) => {
      const hierarchy = branch.split('/')
      if (category[hierarchy.length.toString()]) {
        category[hierarchy.length.toString()].push(branch)
      } else {
        category[hierarchy.length.toString()] = [branch]
      }
    })
    const indices = Object.keys(category)
    indices.forEach(index => {
      category[index] = category[index].sort((a: string, b: string) => {
        a = a.toLowerCase()
        b = b.toLowerCase()
        return a.localeCompare(b)
      })
    })
    indices.forEach((index: string) => {
      toReturn = toReturn.concat(category[index])
    })
    return toReturn
  }
  const branchesResponse = await apiGetBranches(repository)
  if (branchesResponse.success) {
    const branches = branchesResponse.result.values.map((value: any) => {
      return value.name
    })
    return createResponse(sortBranches(branches))
  } else {
    return branchesResponse
  }
}

//TODO: fix answer cached?, did not tag HEAD commit, only after reloading
export async function setTag(
  repository: string,
  branch: string,
  tag: string
): Promise<Result<TagResponse> | APIResponse> {
  const commitResponse = await apiGetHEADofBranch(repository, branch)
  if (commitResponse.success) {
    const commit = commitResponse.result.target.hash
    const setTagResponse = await apiSetCommitTag(repository, tag, commit)
    if (setTagResponse.success) {
      return createResponse({ tag: tag, commit: commit, repository: repository })
    } else {
      return setTagResponse
    }
  } else {
    return commitResponse
  }
}

export async function getPipelineStateOfHEAD(
  repository: string,
  branch: string
): Promise<Result<PipelineState> | APIResponse> {
  const commit = await apiGetHEADofBranch(repository, branch)
  if (commit.success) {
    const pipelineState = await getPipelineState(repository, commit.result.target.hash)
    if (pipelineState.success) {
      if (pipelineState.result.values) {
        pipelineState.result = State.notFound
      }
    }
    return pipelineState
  }
  return commit
}

export async function getPipelineState(
  repository: string,
  commit: string
): Promise<Result<PipelineState> | APIResponse> {
  const pipelinesResponse = await apiPipeline(repository)
  if (pipelinesResponse.success) {
    const pipeline = pipelinesResponse.result.values.find((pipeline: any) => {
      return pipeline.target.commit.hash === commit
    })
    if (pipeline) {
      if (pipeline.state.name === 'COMPLETED') {
        return createResponse(pipeline.state.result.name as PipelineState)
      } else {
        return createResponse(pipeline.state.name as PipelineState)
      }
    }
  }
  return pipelinesResponse
}

export async function updateConfig(
  branch: string,
  deploymentTag: string,
  toDeploy: DeploymentProperties[]
): Promise<Result<TagResponse> | APIResponse> {
  async function configureFeaturesJSON() {
    let update = false
    const responseFeaturesJSON = await getFeaturesJSONRaw(branch)
    if (responseFeaturesJSON.success) {
      const featuresJSON: FeatureJson = responseFeaturesJSON.result
      toDeploy.forEach((component: DeploymentProperties) => {
        const jsonComponents = Object.keys(featuresJSON.components)
        const name = jsonComponents.find(name => name === component.name)
        if (name) {
          if (featuresJSON.components[name].release !== component.tag) {
            update = true
            featuresJSON.components[name].release = component.tag
          }
        }
      })
      return update ? createResponse(featuresJSON) : createResponse(false)
    } else {
      return responseFeaturesJSON as APIResponse
    }
  }

  const newFeatureJson = await configureFeaturesJSON()
  if (newFeatureJson.success) {
    if (newFeatureJson.result) {
      const data: FormData = new FormData()
      data.set('branch', branch)
      data.set('message', 'update features.json to release: ' + deploymentTag)
      data.set(configFile, JSON.stringify(newFeatureJson.result, null, 2))
      const alterFileResponse = await apiPushFile(configRepository, data)
      if (alterFileResponse.success) {
        const commitResponse = await apiGetHEADofBranch(configRepository, branch)
        if (commitResponse.success) {
          const commit: string = commitResponse.result.target.hash
          const tagResponse = await apiSetCommitTag(configRepository, deploymentTag, commit)
          if (tagResponse.success) {
            return createResponse({ tag: deploymentTag, commit: commit, repository: configRepository })
          } else {
            return tagResponse
          }
        } else {
          return commitResponse
        }
      } else {
        return alterFileResponse
      }
    } else {
      return { success: false, code: 500, result: 'Nothing to release!' } as APIResponse
    }
  } else {
    return newFeatureJson as APIResponse
  }
}

export async function getFeaturesJSON(branch: string): Promise<Result<FeatureJsonProperties> | APIResponse> {
  const file = await getFeaturesJSONRaw(branch)
  if (file.success) {
    const response: FeatureJsonProperties = { system: '', components: [] }
    const featuresJson: FeatureJson = file.result
    const components = Object.keys(featuresJson.components)
    const values = Object.values(featuresJson.components)
    response.components = components
      .map((component: string, index: number) => {
        if (values[index].repo) {
          return {
            branch: values[index].branch,
            name: component,
            repository: values[index].repo,
            tag: values[index].release
          }
        }
      })
      .filter(component => component) as FeatureJsonComponent[]
    response.system = file.result.system.prefix
    return createResponse(response)
  } else {
    return file
  }
}

async function getFeaturesJSONRaw(branch: string): Promise<Result<FeatureJson> | APIResponse> {
  const commitResponse = await apiGetHEADofBranch(configRepository, branch)
  if (commitResponse.success) {
    const commit = commitResponse.result.target.hash
    const fileResponse = await apiGetFile(configRepository, commit, configFile)
    if (fileResponse.success) {
      return createResponse(fileResponse.result)
    } else {
      return fileResponse
    }
  } else {
    return commitResponse
  }
}

export async function getPipelineSteps(branch: string): Promise<Result<Selector[]> | APIResponse> {
  const commitResponse = await apiGetHEADofBranch(configRepository, branch)
  if (commitResponse.success) {
    const commit = commitResponse.result.target.hash
    const file = '/bitbucket-pipelines.yml'
    const fileResponse = await apiGetFile(configRepository, commit, file)
    if (fileResponse.success) {
      const pipelinesYAML: BitBucketPipelinesYaml = yaml.parse(fileResponse.result)
      const pipelineOptions: string[] = Object.keys(pipelinesYAML.pipelines)
      const toReturn: Selector[] = []
      pipelineOptions.forEach((option: string) => {
        const steps: string[] = Object.keys(pipelinesYAML.pipelines[option])
        steps.forEach((step: string) => {
          toReturn.push({ type: option, pattern: step })
        })
      })
      return createResponse(toReturn)
    } else {
      return fileResponse
    }
  } else {
    return commitResponse
  }
}

export async function getBranchingModel(repository: string): Promise<Result<BranchingModelResponse> | APIResponse> {
  const branchingModelRequest = await apiGetBranchModel(repository)
  if (branchingModelRequest.success) {
    const production = branchingModelRequest.result.production
      ? branchingModelRequest.result.production.name
      : undefined
    const development = branchingModelRequest.result.development
      ? branchingModelRequest.result.development.name
      : undefined
    //to handle wrongly configured repositories
    const response = { productionBranch: '', developmentBranch: '' }
    if (production && development) {
      response.productionBranch = production
      response.developmentBranch = development
    }
    if (!production && development) {
      response.productionBranch = development
    }
    if (production && !development) {
      response.productionBranch = production
    }
    return createResponse(response)
  } else {
    return branchingModelRequest
  }
}

export async function startDeploymentPipeline(branch: string, step: Selector): Promise<Result<string> | APIResponse> {
  const commitResponse = await apiGetHEADofBranch(configRepository, branch)
  if (commitResponse.success) {
    const commit = commitResponse.result.target.hash
    const pipelineStartResponse = await apiStartPipeline(configRepository, branch, commit, step)
    if (pipelineStartResponse.success) {
      return createResponse(commit)
    } else {
      return pipelineStartResponse
    }
  } else {
    return commitResponse
  }
}

function getFormattedDate() {
  function exactTwoDigits(toConvert: number): string {
    if (toConvert > 9) {
      const length = toConvert.toString().length
      return toConvert.toString().slice(length - 2, length)
    } else {
      return '0' + toConvert.toString()
    }
  }
  const currentDate = new Date(Date.now())
  const year = exactTwoDigits(currentDate.getFullYear())
  const month = exactTwoDigits(currentDate.getMonth() + 1)
  const day = exactTwoDigits(currentDate.getDate())
  return year + month + day
}

function getSuffix(currentTag: string, latestTag: string): string {
  let newSuffix = 2
  if (latestTag) {
    const suffix = latestTag.replace(currentTag, '').slice(1)
    if (suffix) {
      const lastSuffix = Number(suffix)
      newSuffix = lastSuffix + 1
    }
    return '-' + newSuffix
  }
  return ''
}

export async function getDeploymentTag(repository: string, prefix: string): Promise<Result<string> | APIResponse> {
  let tag = prefix + '-' + getFormattedDate()
  let suffix = ''
  const getTagResponse = await apiGetLatestTag(repository, tag)
  if (getTagResponse.success) {
    if (getTagResponse.result.values.length > 0) {
      suffix = getSuffix(tag, getTagResponse.result.values[0].name)
    }
    tag = tag + suffix
    return createResponse(tag)
  } else {
    return getTagResponse
  }
}

export async function getAvailableTag(repository: string, branch: string): Promise<Result<string> | APIResponse> {
  let tag = releaseTag
  let suffix = ''
  const regTicketId = new RegExp('[A-Z]*-[0-9]*', 'g')
  switch (branch) {
    case Branch.master:
    case Branch.main:
      tag += getFormattedDate()
      break
    case Branch.develop:
      tag += getFormattedDate() + developTag
      break
    default:
      tag += regTicketId.exec(branch)
      break
  }
  const getTagResponse = await getLatestTags(repository, branch)
  if (getTagResponse.success) {
    getTagResponse.result = getTagResponse.result.filter((result: TagResponse) => result.tag.includes(tag))
    if (getTagResponse.result.length > 0) {
      suffix = getSuffix(tag, getTagResponse.result[0].tag)
    }
    tag = tag + suffix
    return createResponse(tag)
  } else {
    return getTagResponse
  }
}

//returns true if changes between HEAD commits of branches exist
export async function getDiffStat(
  repository: string,
  sourceBranch: string,
  destinationBranch: string
): Promise<Result<boolean> | APIResponse> {
  const sourceCommitRequest = apiGetHEADofBranch(repository, sourceBranch)
  const destinationCommitRequest = apiGetHEADofBranch(repository, destinationBranch)
  const responses = await Promise.all([sourceCommitRequest, destinationCommitRequest])
  if (responses.every(response => response.success)) {
    const sourceCommit = responses[0].result.target.hash
    const destinationCommit = responses[1].result.target.hash
    const diffStat = await apiDiffStat(repository, sourceCommit, destinationCommit)
    if (diffStat.success) {
      return diffStat.result.values.length !== 0 ? createResponse(true) : createResponse(false)
    } else {
      return diffStat
    }
  } else {
    if (!responses[0].success) {
      return responses[0]
    } else {
      return responses[1]
    }
  }
}

export async function getLatestTags(repository: string, branch: string): Promise<Result<TagResponse[]> | APIResponse> {
  let latestTagsResponse = null
  switch (branch) {
    case Branch.master:
    case Branch.main:
      latestTagsResponse = await apiGetLatestMasterTags(repository)
      break
    case Branch.develop:
      latestTagsResponse = await apiGetLatestDevelopTags(repository)
      break
    default:
      const regTicketId = new RegExp('[A-Z]*-[0-9]*', 'g')
      const tag = regTicketId.exec(branch)
      if (tag) {
        latestTagsResponse = await apiGetLatestFeatureTags(repository, tag[0])
      } else {
        latestTagsResponse = await apiGetLatestFeatureTags(repository)
      }
      break
  }
  if (latestTagsResponse.success) {
    const result: TagResponse[] = latestTagsResponse.result.values.map((response: any) => {
      return { tag: response.name, commit: response.target.hash, repository: repository }
    })
    const regexDev = /release-[0-9]{6}-dev/
    const regexMaster = /release-[0-9]{6}/
    const regexFeature = /release-[A-Z]*-[0-9]*/
    const regexCounter = /[-,\\.]{0,1}/
    return createResponse(result.sort((a, b) => {
      const a1 = a.tag.replace(regexDev, '').replace(regexMaster, '').replace(regexFeature, '')
      const b1 = b.tag.replace(regexDev, '').replace(regexMaster, '').replace(regexFeature, '')

      const a2 = a1.length === 0 ? a.tag : a.tag.substring(0, a.tag.lastIndexOf(a1))
      const b2 = b1.length === 0 ? b.tag : b.tag.substring(0, b.tag.lastIndexOf(b1))

      if (a2 === b2) {
        const a3 = a1.length === 0 ? 0 : Number(a1.replace(regexCounter, ''))
        const b3 = b1.length === 0 ? 0 : Number(b1.replace(regexCounter, ''))
        return a3 > b3 ? -1 : 1
      } else {
        return a2 > b2 ? -1 : 1
      }
    }))
  } else {
    return latestTagsResponse
  }
}

export async function mergeBranches(
  repository: string,
  sourceBranch: string,
  destinationBranch: string,
  title: string,
  message: string,
  close: boolean
): Promise<Result<String> | APIResponse> {
  const createPullRequestResponse = await apiCreatePullRequest(
    repository,
    sourceBranch,
    destinationBranch,
    title,
    close
  )
  if (createPullRequestResponse.success) {
    const pullRequestId: number = createPullRequestResponse.result.id
    const mergePullRequestResponse = await apiMergePullRequest(repository, pullRequestId, message, close)
    if (mergePullRequestResponse.success) {
      return createResponse(mergePullRequestResponse.result.merge_commit.hash)
    } else {
      return mergePullRequestResponse
    }
  } else {
    return createPullRequestResponse
  }
}
